import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { LifeBuoy, Loader2, MessageCircle, Paperclip, Plus, Send, X } from "lucide-react";
import axiosInstance from "../utils/axiosInstance";
import { getApiErrorLines } from "../utils/formErrors";
import { toastApiError, toastErrors } from "../utils/toastErrors";
import { toPersianDigits } from "../utils/numberFormat";

// Chat with the support team. This instance relays every call to the
// management platform (support/views.py); messages live there.

const LIST_REFRESH_MS = 30 * 1000;
const THREAD_REFRESH_MS = 5 * 1000;
const MAX_BODY = 4000;
const MAX_ATTACHMENT_BYTES = 5 * 1024 * 1024;
const ATTACHMENT_TYPES = ["image/png", "image/jpeg", "application/pdf"];
const OPTIONS = { skipCompany: true };

const STATUS = {
  open: { label: "در انتظار پشتیبانی", tone: "bg-amber-100 text-amber-800" },
  waiting_customer: { label: "پاسخ داده شد", tone: "bg-emerald-100 text-emerald-800" },
  closed: { label: "بسته شده", tone: "bg-slate-200 text-slate-600" },
};

const LABELS = { subject: "موضوع", body: "پیام", attachment: "فایل پیوست" };

const when = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("fa-IR", { dateStyle: "short", timeStyle: "short" });
  } catch {
    return "";
  }
};

const attachmentProblem = (file) => {
  if (!file) return "";
  if (!ATTACHMENT_TYPES.includes(file.type)) return "فایل پیوست: فقط تصویر PNG یا JPEG و فایل PDF مجاز است.";
  if (file.size > MAX_ATTACHMENT_BYTES) return "فایل پیوست: حجم فایل حداکثر ۵ مگابایت است.";
  return "";
};

const asForm = (fields) => {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    if (value !== null && value !== undefined) form.append(key, value);
  });
  return form;
};

function StatusBadge({ status }) {
  const info = STATUS[status] || STATUS.open;
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] ${info.tone}`}>{info.label}</span>;
}

function AttachmentPicker({ file, onChange, inputRef }) {
  return (
    <div className="flex items-center gap-2 text-xs text-slate-600">
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-lg border px-2 py-1 hover:bg-slate-50">
        <Paperclip size={14} />
        پیوست
        <input
          ref={inputRef}
          type="file"
          accept=".png,.jpg,.jpeg,.pdf"
          className="hidden"
          onChange={(event) => onChange(event.target.files?.[0] || null)}
        />
      </label>
      {file ? (
        <span className="flex min-w-0 items-center gap-1">
          <span className="truncate" dir="ltr">{file.name}</span>
          <button type="button" aria-label="حذف پیوست" onClick={() => onChange(null)} className="text-slate-400 hover:text-rose-600">
            <X size={14} />
          </button>
        </span>
      ) : null}
    </div>
  );
}

export default function SupportPage() {
  const [conversations, setConversations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [unavailable, setUnavailable] = useState("");
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [threadLoading, setThreadLoading] = useState(false);
  const [composing, setComposing] = useState(false);
  const [reply, setReply] = useState("");
  const [replyFile, setReplyFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [draft, setDraft] = useState({ subject: "", body: "" });
  const [draftFile, setDraftFile] = useState(null);
  const replyFileInput = useRef(null);
  const draftFileInput = useRef(null);
  const threadEnd = useRef(null);
  const lastId = useRef(0);

  // 503 means the platform is not set up or unreachable: explain on the page.
  const handleUnavailable = useCallback((error) => {
    if (error?.response?.status === 503) {
      setUnavailable(getApiErrorLines(error)[0]);
      return true;
    }
    return false;
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get("/support/conversations/", OPTIONS);
      setConversations(data?.results || []);
      setUnavailable("");
    } catch (error) {
      if (!handleUnavailable(error)) toastApiError(error, {}, "دریافت گفتگوها ناموفق بود.");
    } finally {
      setListLoading(false);
    }
  }, [handleUnavailable]);

  const markRead = useCallback(async (id) => {
    try {
      await axiosInstance.post(`/support/conversations/${id}/read/`, {}, OPTIONS);
      setConversations((list) => list.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c)));
      window.dispatchEvent(new Event("support:read")); // refresh the menu badge
    } catch {
      // The unread count corrects itself on the next refresh.
    }
  }, []);

  // New messages since the last one we have, so polling stays cheap.
  const loadMessages = useCallback(async (id, { reset = false } = {}) => {
    if (reset) {
      lastId.current = 0;
      setThreadLoading(true);
    }
    try {
      const params = lastId.current ? { after: lastId.current } : {};
      const { data } = await axiosInstance.get(`/support/conversations/${id}/messages/`, { ...OPTIONS, params });
      const fresh = data?.results || [];
      if (data?.conversation) {
        setSelected(data.conversation);
        setConversations((list) => list.map((c) => (c.id === id ? { ...c, ...data.conversation } : c)));
      }
      if (fresh.length) {
        lastId.current = fresh[fresh.length - 1].id;
        setMessages((current) => (reset ? fresh : [...current, ...fresh]));
        if (fresh.some((message) => message.sender === "support")) markRead(id);
      } else if (reset) {
        setMessages([]);
      }
    } catch (error) {
      if (reset && !handleUnavailable(error)) toastApiError(error, {}, "دریافت پیام‌ها ناموفق بود.");
    } finally {
      if (reset) setThreadLoading(false);
    }
  }, [markRead, handleUnavailable]);

  useEffect(() => {
    loadConversations();
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") loadConversations();
    }, LIST_REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadConversations]);

  useEffect(() => {
    if (!selected?.id) return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") loadMessages(selected.id);
    }, THREAD_REFRESH_MS);
    return () => clearInterval(timer);
  }, [selected?.id, loadMessages]);

  useEffect(() => {
    threadEnd.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  const open = (conversation) => {
    setComposing(false);
    setSelected(conversation);
    setReply("");
    setReplyFile(null);
    loadMessages(conversation.id, { reset: true });
  };

  const startConversation = async (event) => {
    event.preventDefault();
    const problems = [];
    if (!draft.subject.trim()) problems.push("موضوع: الزامی است.");
    if (!draft.body.trim() && !draftFile) problems.push("پیام: متن پیام یا یک فایل پیوست لازم است.");
    if (draft.body.length > MAX_BODY) problems.push(`پیام: حداکثر ${toPersianDigits(MAX_BODY)} نویسه مجاز است.`);
    if (attachmentProblem(draftFile)) problems.push(attachmentProblem(draftFile));
    if (problems.length) {
      toastErrors(problems);
      return;
    }
    setSending(true);
    try {
      const { data } = await axiosInstance.post(
        "/support/conversations/",
        asForm({ subject: draft.subject.trim(), body: draft.body.trim(), attachment: draftFile }),
        OPTIONS,
      );
      toast.success("گفتگو ثبت شد؛ پشتیبانی به‌زودی پاسخ می‌دهد.");
      setDraft({ subject: "", body: "" });
      setDraftFile(null);
      if (draftFileInput.current) draftFileInput.current.value = "";
      await loadConversations();
      if (data?.id) open(data);
    } catch (error) {
      if (!handleUnavailable(error)) toastApiError(error, LABELS, "ثبت گفتگو ناموفق بود.");
    } finally {
      setSending(false);
    }
  };

  const sendReply = async (event) => {
    event.preventDefault();
    if (!selected) return;
    const problems = [];
    if (!reply.trim() && !replyFile) problems.push("پیام: متن پیام یا یک فایل پیوست لازم است.");
    if (reply.length > MAX_BODY) problems.push(`پیام: حداکثر ${toPersianDigits(MAX_BODY)} نویسه مجاز است.`);
    if (attachmentProblem(replyFile)) problems.push(attachmentProblem(replyFile));
    if (problems.length) {
      toastErrors(problems);
      return;
    }
    setSending(true);
    try {
      await axiosInstance.post(
        `/support/conversations/${selected.id}/messages/`,
        asForm({ body: reply.trim(), attachment: replyFile }),
        OPTIONS,
      );
      setReply("");
      setReplyFile(null);
      if (replyFileInput.current) replyFileInput.current.value = "";
      await loadMessages(selected.id);
    } catch (error) {
      if (!handleUnavailable(error)) toastApiError(error, LABELS, "ارسال پیام ناموفق بود.");
    } finally {
      setSending(false);
    }
  };

  // A plain link would not carry the sign-in token, so download through axios.
  const download = async (attachment) => {
    try {
      const { data } = await axiosInstance.get(`/support/attachments/${attachment.id}/`, {
        ...OPTIONS,
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.name || "attachment";
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (error) {
      toastApiError(error, {}, "دریافت فایل پیوست ناموفق بود.");
    }
  };

  return (
    <div dir="rtl" className="mx-auto mt-20 max-w-6xl px-3 pb-10 font-vazir md:mt-24">
      <header className="mb-5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white">
            <LifeBuoy size={22} />
          </span>
          <div>
            <h1 className="text-xl font-bold text-slate-900 md:text-2xl">پشتیبانی</h1>
            <p className="text-sm text-slate-500">سؤال یا مشکل خود را بنویسید؛ پاسخ پشتیبانی همین‌جا نمایش داده می‌شود.</p>
          </div>
        </div>
        {!unavailable && (
          <button
            type="button"
            onClick={() => {
              setComposing(true);
              setSelected(null);
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            <Plus size={16} />
            گفتگوی جدید
          </button>
        )}
      </header>

      {unavailable ? (
        <div role="status" className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm leading-7 text-amber-900">
          {unavailable}
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-[300px_minmax(0,1fr)]">
          {/* Conversations */}
          <aside className="rounded-2xl border bg-white">
            <h2 className="border-b px-4 py-3 text-sm font-semibold text-slate-700">گفتگوها</h2>
            {listLoading ? (
              <p className="flex items-center gap-2 p-4 text-sm text-slate-500">
                <Loader2 size={16} className="animate-spin" /> در حال دریافت…
              </p>
            ) : conversations.length === 0 ? (
              <p className="p-4 text-sm text-slate-500">هنوز گفتگویی ندارید.</p>
            ) : (
              <ul className="max-h-[65vh] divide-y overflow-y-auto">
                {conversations.map((conversation) => (
                  <li key={conversation.id}>
                    <button
                      type="button"
                      onClick={() => open(conversation)}
                      className={`w-full px-4 py-3 text-right hover:bg-slate-50 ${selected?.id === conversation.id ? "bg-blue-50" : ""}`}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="truncate text-sm font-medium text-slate-900">{conversation.subject}</span>
                        {conversation.unread_count > 0 && (
                          <span className="rounded-full bg-rose-600 px-1.5 text-[11px] text-white">
                            {toPersianDigits(conversation.unread_count)}
                          </span>
                        )}
                      </span>
                      <span className="mt-1 flex items-center justify-between gap-2">
                        <span className="truncate text-xs text-slate-500">{conversation.last_message_preview}</span>
                        <StatusBadge status={conversation.status} />
                      </span>
                      {conversation.started_by?.name && (
                        <span className="mt-1 block text-[11px] text-slate-400">
                          {conversation.started_by.name} · {when(conversation.last_message_at || conversation.updated_at)}
                        </span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </aside>

          {/* Thread / new conversation */}
          <section className="flex min-h-[60vh] flex-col rounded-2xl border bg-white">
            {composing ? (
              <form onSubmit={startConversation} noValidate className="flex flex-1 flex-col gap-3 p-4">
                <h2 className="text-base font-semibold text-slate-800">گفتگوی جدید</h2>
                <input
                  value={draft.subject}
                  maxLength={200}
                  onChange={(event) => setDraft((d) => ({ ...d, subject: event.target.value }))}
                  placeholder="موضوع، مثلاً «خطا در ثبت فاکتور»"
                  className="rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
                <textarea
                  value={draft.body}
                  maxLength={MAX_BODY}
                  rows={7}
                  onChange={(event) => setDraft((d) => ({ ...d, body: event.target.value }))}
                  placeholder="مشکل یا سؤال خود را کامل بنویسید. تصویر صفحه کمک زیادی می‌کند."
                  className="flex-1 resize-y rounded-xl border px-3 py-2 text-sm leading-7 focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
                <div className="flex items-center justify-between gap-3">
                  <AttachmentPicker file={draftFile} onChange={setDraftFile} inputRef={draftFileInput} />
                  <button
                    type="submit"
                    disabled={sending}
                    className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                  >
                    {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                    ارسال
                  </button>
                </div>
              </form>
            ) : !selected ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-2 p-8 text-center text-sm text-slate-500">
                <MessageCircle size={32} className="text-slate-300" />
                یک گفتگو را انتخاب کنید یا گفتگوی جدیدی شروع کنید.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
                  <h2 className="truncate text-base font-semibold text-slate-800">{selected.subject}</h2>
                  <StatusBadge status={selected.status} />
                </div>
                <div className="max-h-[55vh] flex-1 space-y-3 overflow-y-auto p-4">
                  {threadLoading ? (
                    <p className="flex items-center gap-2 text-sm text-slate-500">
                      <Loader2 size={16} className="animate-spin" /> در حال دریافت پیام‌ها…
                    </p>
                  ) : (
                    messages.map((message) => {
                      const mine = message.sender === "customer";
                      return (
                        <div key={message.id} className={`flex ${mine ? "justify-start" : "justify-end"}`}>
                          <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm leading-7 ${mine ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-900"}`}>
                            <p className={`mb-1 text-[11px] ${mine ? "text-blue-100" : "text-slate-500"}`}>
                              {mine ? message.author_name : `پشتیبانی${message.author_name ? ` · ${message.author_name}` : ""}`} · {when(message.created_at)}
                            </p>
                            {message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}
                            {(message.attachments || []).map((attachment) => (
                              <button
                                key={attachment.id}
                                type="button"
                                onClick={() => download(attachment)}
                                className={`mt-1 flex items-center gap-1 text-xs underline ${mine ? "text-blue-50" : "text-blue-700"}`}
                              >
                                <Paperclip size={12} />
                                <span dir="ltr">{attachment.name}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={threadEnd} />
                </div>
                <form onSubmit={sendReply} noValidate className="space-y-2 border-t p-3">
                  {selected.status === "closed" && (
                    <p className="text-xs text-slate-500">این گفتگو بسته شده است؛ با ارسال پیام دوباره باز می‌شود.</p>
                  )}
                  <textarea
                    value={reply}
                    maxLength={MAX_BODY}
                    rows={3}
                    onChange={(event) => setReply(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) sendReply(event);
                    }}
                    placeholder="پیام خود را بنویسید… (Ctrl+Enter برای ارسال)"
                    className="w-full resize-y rounded-xl border px-3 py-2 text-sm leading-7 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  />
                  <div className="flex items-center justify-between gap-3">
                    <AttachmentPicker file={replyFile} onChange={setReplyFile} inputRef={replyFileInput} />
                    <button
                      type="submit"
                      disabled={sending}
                      className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                      ارسال
                    </button>
                  </div>
                </form>
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
